import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { showToast } from '../utils/toast';

type FsMode = 'read' | 'readwrite';

type PendingLocalWriteIntent = {
  status?: string;
  operation?: string;
  path?: string;
  content?: string;
  find?: string;
  replace?: string;
  count?: number;
  search_block?: string;
  replace_block?: string;
  [key: string]: any;
};

type LocalEntry = {
  name: string;
  type: 'file' | 'folder';
  path: string;
};

type LocalWriteResult = {
  status: 'ok' | 'no_change';
  path: string;
  bytes?: number;
  replaced?: number;
  message?: string;
};

interface LocalMountContextValue {
  isSupported: boolean;
  localModeEnabled: boolean;
  mountReady: boolean;
  mountName: string;
  mountError: string;
  setLocalModeEnabled: (enabled: boolean) => void;
  mountLocalFolder: () => Promise<boolean>;
  disconnectLocalFolder: () => Promise<void>;
  listDirectory: (path?: string) => Promise<LocalEntry[]>;
  readTextFile: (path: string) => Promise<string>;
  readFile?: (path: string) => Promise<File>;
  writeTextFile: (path: string, content: string) => Promise<LocalWriteResult>;
  writeOriginalFile: (path: string, content: Blob) => Promise<LocalWriteResult>;
  deletePath: (path: string) => Promise<void>;
  applyPendingLocalWrite: (intent: PendingLocalWriteIntent) => Promise<LocalWriteResult>;
}

const LocalMountContext = createContext<LocalMountContextValue | null>(null);

const IDB_DB_NAME = 'msapling-local-mount';
const IDB_STORE = 'handles';
const IDB_KEY = 'root';

const isFileSystemAccessSupported = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof (window as any).showDirectoryPicker === 'function';
};

const openDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
  });

const idbGet = async (): Promise<FileSystemDirectoryHandle | null> => {
  const db = await openDb();
  return await new Promise<FileSystemDirectoryHandle | null>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readonly');
    const store = tx.objectStore(IDB_STORE);
    const req = store.get(IDB_KEY);
    req.onsuccess = () => resolve((req.result as FileSystemDirectoryHandle) || null);
    req.onerror = () => reject(req.error || new Error('IndexedDB read failed'));
  });
};

const idbSet = async (handle: FileSystemDirectoryHandle): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    const req = store.put(handle, IDB_KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error || new Error('IndexedDB write failed'));
  });
};

const idbDelete = async (): Promise<void> => {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    const req = store.delete(IDB_KEY);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error || new Error('IndexedDB delete failed'));
  });
};

const normalizePath = (rawPath: string): string[] => {
  const normalized = String(rawPath || '').replace(/\\/g, '/').replace(/^\/+/, '');
  return normalized
    .split('/')
    .map((part) => part.trim())
    .filter((part) => part && part !== '.' && part !== '..');
};

const resolveDir = async (
  root: FileSystemDirectoryHandle,
  segments: string[],
  create: boolean,
): Promise<FileSystemDirectoryHandle> => {
  let current = root;
  for (const segment of segments) {
    current = await current.getDirectoryHandle(segment, { create });
  }
  return current;
};

const verifyPermission = async (
  handle: FileSystemDirectoryHandle,
  mode: FsMode,
  requestIfNeeded: boolean,
): Promise<boolean> => {
  const fsHandle: any = handle as any;
  const descriptor: any = { mode };
  const queryState = typeof fsHandle.queryPermission === 'function'
    ? await fsHandle.queryPermission(descriptor)
    : 'granted';
  if (queryState === 'granted') return true;
  if (!requestIfNeeded) return false;
  const reqState = typeof fsHandle.requestPermission === 'function'
    ? await fsHandle.requestPermission(descriptor)
    : queryState;
  return reqState === 'granted';
};

export const LocalMountProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [directoryHandle, setDirectoryHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [localModeEnabled, setLocalModeEnabledState] = useState<boolean>(false);
  const [mountError, setMountError] = useState<string>('');

  const isSupported = isFileSystemAccessSupported();
  const mountReady = !!directoryHandle;
  const mountName = String(directoryHandle?.name || '');

  useEffect(() => {
    let cancelled = false;
    if (!isSupported) return;
    (async () => {
      try {
        const restored = await idbGet();
        if (!restored || cancelled) return;
        const granted = await verifyPermission(restored, 'readwrite', false);
        if (cancelled) return;
        if (granted) {
          setDirectoryHandle(restored);
          setMountError('');
          return;
        }
        setMountError('Local folder permission expired. Reconnect mount.');
      } catch {
        if (!cancelled) setMountError('');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSupported]);

  const setLocalModeEnabled = useCallback(
    (enabled: boolean) => {
      if (enabled && !directoryHandle) {
        setMountError('Connect a local folder before enabling Local Mode.');
      } else {
        setMountError('');
      }
      setLocalModeEnabledState(Boolean(enabled));
    },
    [directoryHandle],
  );

  const mountLocalFolder = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      setMountError('Local folder mount requires Chrome or Edge.');
      return false;
    }
    try {
      const picked = await (window as any).showDirectoryPicker({ mode: 'readwrite' });
      const granted = await verifyPermission(picked, 'readwrite', true);
      if (!granted) {
        setMountError('Local folder permission was denied.');
        return false;
      }
      await idbSet(picked);
      setDirectoryHandle(picked);
      setLocalModeEnabledState(true);
      setMountError('');
      return true;
    } catch (err: any) {
      const message = String(err?.message || 'Local folder selection cancelled.');
      setMountError(message);
      return false;
    }
  }, [isSupported]);

  const disconnectLocalFolder = useCallback(async (): Promise<void> => {
    setLocalModeEnabledState(false);
    setDirectoryHandle(null);
    setMountError('');
    try {
      await idbDelete();
    } catch {
      // Best effort.
    }
  }, []);

  const listDirectory = useCallback(
    async (path = ''): Promise<LocalEntry[]> => {
      if (!directoryHandle) return [];
      const segments = normalizePath(path);
      const dir = await resolveDir(directoryHandle, segments, false);
      const entries: LocalEntry[] = [];
      const iterator = (dir as any).entries();
      while (true) {
        const next = await iterator.next();
        if (next.done) break;
        const [name, handle] = next.value as [string, FileSystemHandle];
        const type = handle.kind === 'directory' ? 'folder' : 'file';
        entries.push({
          name,
          type,
          path: [...segments, name].join('/'),
        });
      }
      entries.sort((a, b) => {
        if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      return entries;
    },
    [directoryHandle],
  );

  const readFile = useCallback(
    async (path: string): Promise<File> => {
      if (!directoryHandle) throw new Error('Local folder is not connected.');
      const segments = normalizePath(path);
      if (!segments.length) throw new Error('Path is required.');
      const fileName = segments[segments.length - 1];
      const dir = await resolveDir(directoryHandle, segments.slice(0, -1), false);
      const fileHandle = await dir.getFileHandle(fileName);
      const file = await fileHandle.getFile();
      return file;
    },
    [directoryHandle],
  );
  const readTextFile = useCallback(async (path: string): Promise<string> => (await readFile(path)).text(), [readFile]);

  const writeTextFile = useCallback(
    async (path: string, content: string): Promise<LocalWriteResult> => {
      if (!directoryHandle) throw new Error('Local folder is not connected.');
      const segments = normalizePath(path);
      if (!segments.length) throw new Error('Path is required.');
      const fileName = segments[segments.length - 1];
      const dir = await resolveDir(directoryHandle, segments.slice(0, -1), true);
      const fileHandle = await dir.getFileHandle(fileName, { create: true });
      const writable = await fileHandle.createWritable();
      await writable.write(content ?? '');
      await writable.close();
      return {
        status: 'ok',
        path: segments.join('/'),
        bytes: new TextEncoder().encode(content ?? '').length,
      };
    },
    [directoryHandle],
  );

  const writeOriginalFile = useCallback(async (path: string, content: Blob): Promise<LocalWriteResult> => {
    if (!directoryHandle) throw new Error('Local folder is not connected.');
    const segments = normalizePath(path);
    if (!segments.length) throw new Error('Path is required.');
    const dir = await resolveDir(directoryHandle, segments.slice(0, -1), true);
    const file = await dir.getFileHandle(segments[segments.length - 1], {create: true});
    const writable = await file.createWritable();
    await writable.write(content); await writable.close();
    return {status: 'ok', path: segments.join('/'), bytes: content.size};
  }, [directoryHandle]);

  const deletePath = useCallback(
    async (path: string): Promise<void> => {
      if (!directoryHandle) throw new Error('Local folder is not connected.');
      const segments = normalizePath(path);
      if (!segments.length) throw new Error('Path is required.');
      const target = segments[segments.length - 1];
      const dir = await resolveDir(directoryHandle, segments.slice(0, -1), false);
      await dir.removeEntry(target, { recursive: true });
    },
    [directoryHandle],
  );

  const applyPendingLocalWrite = useCallback(
    async (intent: PendingLocalWriteIntent): Promise<LocalWriteResult> => {
      const operation = String(intent?.operation || '').trim() || 'write_file';
      const path = String(intent?.path || '').trim();
      if (!path) throw new Error('Missing local write path.');

      if (operation === 'write_file') {
        return await writeTextFile(path, String(intent?.content ?? ''));
      }

      if (operation === 'replace_text') {
        const source = await readTextFile(path);
        const find = String(intent?.find || '');
        const replace = String(intent?.replace ?? '');
        if (!find) throw new Error('Missing find text for replace operation.');
        const countRaw = Number(intent?.count);
        const count = Number.isFinite(countRaw) && countRaw > 0 ? Math.floor(countRaw) : 0;
        const matches = source.split(find).length - 1;
        if (matches <= 0) {
          return { status: 'no_change', path, replaced: 0, message: 'No matches found.' };
        }
        let updated = source;
        if (count > 0) {
          let remaining = count;
          while (remaining > 0) {
            const next = updated.replace(find, replace);
            if (next === updated) break;
            updated = next;
            remaining -= 1;
          }
        } else {
          updated = updated.split(find).join(replace);
        }
        const replaced = count > 0 ? Math.min(matches, count) : matches;
        await writeTextFile(path, updated);
        return { status: 'ok', path, replaced };
      }

      if (operation === 'patch_file') {
        const source = await readTextFile(path);
        const searchBlock = String(intent?.search_block || '');
        const replaceBlock = String(intent?.replace_block ?? '');
        if (!searchBlock) throw new Error('Missing search_block for patch operation.');
        const idx = source.indexOf(searchBlock);
        if (idx < 0) {
          return { status: 'no_change', path, replaced: 0, message: 'Search block not found.' };
        }
        const updated = `${source.slice(0, idx)}${replaceBlock}${source.slice(idx + searchBlock.length)}`;
        await writeTextFile(path, updated);
        return { status: 'ok', path, replaced: 1 };
      }

      throw new Error(`Unsupported local write operation: ${operation}`);
    },
    [readTextFile, writeTextFile],
  );

  const value = useMemo<LocalMountContextValue>(
    () => ({
      isSupported,
      localModeEnabled,
      mountReady,
      mountName,
      mountError,
      setLocalModeEnabled,
      mountLocalFolder,
      disconnectLocalFolder,
      listDirectory,
      readTextFile,
      readFile,
      writeTextFile,
      writeOriginalFile,
      deletePath,
      applyPendingLocalWrite,
    }),
    [
      isSupported,
      localModeEnabled,
      mountReady,
      mountName,
      mountError,
      setLocalModeEnabled,
      mountLocalFolder,
      disconnectLocalFolder,
      listDirectory,
      readTextFile,
      readFile,
      writeTextFile,
      writeOriginalFile,
      deletePath,
      applyPendingLocalWrite,
    ],
  );

  useEffect(() => {
    if (!mountError) return;
    // Only surface the "permission expired" toast when the user has local
    // mode enabled.  Without this guard the toast fires on every page load
    // (including /pricing) for users who previously granted a folder but let
    // the permission lapse — even when they are nowhere near local-mount
    // workflows.
    if (!localModeEnabled) return;
    showToast(mountError, 'warn');
  }, [mountError, localModeEnabled]);

  return <LocalMountContext.Provider value={value}>{children}</LocalMountContext.Provider>;
};

export const useLocalMount = (): LocalMountContextValue => {
  const ctx = useContext(LocalMountContext);
  if (!ctx) {
    throw new Error('useLocalMount must be used inside LocalMountProvider.');
  }
  return ctx;
};
